import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, MapPin, Phone, Mail, User, Building2, ArrowLeft, Lock, Unlock, Ruler, FileText, Eye } from "lucide-react";
import { api, resolveImage, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import ImageCarousel from "../components/ImageCarousel";
import PdfViewerModal from "../components/PdfViewerModal";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const PLACEHOLDER = "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

function priceRange(p) {
  const fmt = (n) => {
    const num = Number(n);
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2).replace(/\.00$/, "")} Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2).replace(/\.00$/, "")} L`;
    return `₹${num.toLocaleString("en-IN")}`;
  };
  if (p.priceMin && p.priceMax) return `${fmt(p.priceMin)} – ${fmt(p.priceMax)}`;
  if (p.priceMin) return `From ${fmt(p.priceMin)}`;
  if (p.priceMax) return `Up to ${fmt(p.priceMax)}`;
  return null;
}

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [pdfViewer, setPdfViewer] = useState({ open: false, url: "", title: "" });
  const [contactRevealed, setContactRevealed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/projects/${id}`);
        if (!cancelled) setProject(data);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e.response?.data?.detail) || "Project not found");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const isAdmin = user?.role === "admin";

  const revealContact = () => {
    if (!user) return toast.error("Please log in to unlock the contact.");
    if (isAdmin) {
      setContactRevealed(true);
      return;
    }
    // Non-admin users must pay to unlock (mirroring listing contact policy).
    toast.info("Unlocking contact requires a plan. Please buy credits from your dashboard.");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex flex-col items-center justify-center px-4">
          <p className="text-lg font-display font-semibold text-ink">{error || "Project not found"}</p>
          <Link to="/" className="text-primary text-sm mt-3 inline-flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> Back to home</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const images = project.images && project.images.length > 0 ? project.images : [PLACEHOLDER];
  const pr = priceRange(project);
  const sizeStr = project.totalArea ? `${project.totalArea} ${project.totalAreaUnit || "Sq Ft"}` : null;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Seo title={`${project.title} — The Pataudi Projects`} description={project.description?.slice(0, 160)} />
      <Header />

      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-ink transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </Link>

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Gallery */}
            <div className="lg:col-span-2 space-y-3" data-testid="project-gallery">
              <div className="relative">
                <ImageCarousel
                  images={images.map((img) => resolveImage(img) || PLACEHOLDER)}
                  alt={project.title}
                  fallback={PLACEHOLDER}
                  className="aspect-[16/10] border border-neutral-200"
                  testid="project-carousel"
                />
                <span className="absolute top-3 left-3 z-10 bg-white/95 backdrop-blur text-neutral-900 text-[11px] font-semibold px-2.5 py-1 rounded-full shadow-sm pointer-events-none">
                  {project.projectType}
                </span>
                <span className="absolute top-3 left-24 z-10 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-sm pointer-events-none">
                  {project.status}
                </span>
              </div>

              {/* PDF chips */}
              {(project.pdfs && project.pdfs.length > 0) && (
                <div className="mt-2 flex flex-wrap gap-2" data-testid="project-pdfs">
                  {project.pdfs.map((raw, i) => {
                    const url = resolveImage(raw) || raw;
                    const nice = decodeURIComponent((raw || "").split("/").pop() || `Document ${i + 1}`).slice(-38);
                    return (
                      <button
                        key={`${raw}-${i}`}
                        type="button"
                        onClick={() => setPdfViewer({ open: true, url, title: nice })}
                        className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 text-primary text-xs font-semibold px-3 py-1.5 hover:bg-primary/10 transition-colors"
                        data-testid={`project-pdf-chip-${i}`}
                      >
                        <FileText className="h-3.5 w-3.5" /> <span className="truncate max-w-[160px]">{nice}</span> <Eye className="h-3.5 w-3.5" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Info sidebar */}
            <aside className="space-y-4" data-testid="project-sidebar">
              <div className="bg-white rounded-2xl border border-neutral-200 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
                <h1 className="font-display font-bold text-2xl text-neutral-900 leading-tight">{project.title}</h1>
                <p className="flex items-center gap-1 text-neutral-500 text-sm mt-2">
                  <MapPin className="h-4 w-4 shrink-0" /> {project.area ? `${project.area}, ` : ""}{project.city}
                </p>
                {pr && (
                  <p className="mt-4 text-2xl font-display font-extrabold text-emerald-700">{pr}</p>
                )}
                {sizeStr && (
                  <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-neutral-700 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-md">
                    <Ruler className="h-3.5 w-3.5" /> {sizeStr}
                  </p>
                )}
              </div>

              {/* Contact card */}
              <div className="bg-white rounded-2xl border border-neutral-200 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Contact</p>
                {contactRevealed ? (
                  <div className="space-y-2 text-sm">
                    {project.contactName && <p className="flex items-center gap-2"><User className="h-4 w-4 text-primary" /> {project.contactName}</p>}
                    {project.contactPhone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-primary" /> {project.contactPhone}</p>}
                    {project.contactEmail && <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /> {project.contactEmail}</p>}
                    {!project.contactName && !project.contactPhone && !project.contactEmail && (
                      <p className="text-muted-foreground">Contact details are not available for this project.</p>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-center mb-3">
                      <p className="text-sm font-medium text-amber-800">Contact is locked</p>
                      <p className="text-xs text-amber-700 mt-0.5">Pay to unlock any contact — buy a plan to continue.</p>
                    </div>
                    <Button onClick={revealContact} className="w-full bg-primary hover:bg-primary/90 text-white gap-1.5" data-testid="project-unlock-contact">
                      {isAdmin ? <><Unlock className="h-4 w-4" /> Reveal Contact (Admin)</> : <><Lock className="h-4 w-4" /> Unlock Contact</>}
                    </Button>
                  </>
                )}
              </div>
            </aside>
          </div>

          {/* Description */}
          <section className="mt-10 max-w-3xl">
            <h2 className="font-display font-bold text-xl text-neutral-900 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" /> About this project
            </h2>
            <p className="mt-3 text-neutral-700 whitespace-pre-wrap leading-relaxed" data-testid="project-description">
              {project.description}
            </p>
          </section>
        </div>
      </main>

      <Footer />

      <PdfViewerModal
        open={pdfViewer.open}
        pdfUrl={pdfViewer.url}
        title={pdfViewer.title || `${project.title} — PDF`}
        onClose={() => setPdfViewer({ open: false, url: "", title: "" })}
      />
    </div>
  );
}
